import { Trans } from "@lingui/macro";
import { Trans as TransComponent } from "@lingui/react";
import { FC, useCallback, useState } from "react";
import { Heading, Text } from "@otrium/atoms";
import { Flex } from "@otrium/core";
import { Button } from "src/atoms/Button";
import { CloseButton } from "src/atoms/CloseButton";
import { Modal } from "src/atoms/Modal";
import { Price } from "src/atoms/Price";
import { ProductVariation, SingleProduct } from "src/types/graphql.d";
import {
  useSizeSchemaFilter,
  sizeFitLabelMessages,
  SizeMapping,
} from "src/hooks/useSizeSchemaFilter";
import {
  ContentWrapper,
  Overlay,
  CloseButtonWrapper,
  ProductImage,
  SchemaSelectorRow,
  SchemaSelectorButton,
  SizeListContainer,
  SizeRow,
  BrandSizeCell,
  MappedSizeCell,
  MappedSizeValue,
  StockIndicator,
  NotifyMeIndicator,
  DrawerFooter,
  SchemaDrawerContainer,
  SchemaList,
  SchemaOption,
} from "./MobileSizeSelectorOverlay.styled";
import { SizeFitLabelBanner } from "src/atoms/SizeFitLabelBanner";
import { theme } from "src/theme";
import { BackInStockNotification } from "src/molecules/BackInStockNotification";
import { ConversionItemAttributes } from "src/utils/constructor-beacon";

interface MobileSizeSelectorOverlayProps {
  isOpen: boolean;
  hidden?: boolean;
  onClose: () => void;
  product: SingleProduct;
  variations: ProductVariation[];
  selectedVariationId?: string;
  onSelectVariation: (variationId: string) => void;
  onOpenSizeGuide?: () => void;
  hasSizeGuide?: boolean;
  addToCartLoading?: boolean;
  onAddToCart: () => Promise<boolean>;
  getStockText?: (quantity: number) => React.ReactNode | null;
  sizeFitLabel?: "runs_small" | "runs_large";
  locale?: string;
  selectedSchema?: string;
  onSchemaChange?: (schema: string) => void;
  onBackInStockPopupChange?: (isOpen: boolean) => void;
  cnstrcItem?: ConversionItemAttributes;
}

const MobileSizeSelectorOverlay: FC<MobileSizeSelectorOverlayProps> = ({
  isOpen,
  hidden,
  onClose,
  product,
  variations,
  selectedVariationId,
  onSelectVariation,
  onOpenSizeGuide,
  hasSizeGuide = false,
  addToCartLoading = false,
  onAddToCart,
  getStockText,
  sizeFitLabel,
  locale,
  selectedSchema: externalSchema,
  onSchemaChange,
  onBackInStockPopupChange,
  cnstrcItem,
}) => {
  const [isSchemaDrawerOpen, setIsSchemaDrawerOpen] = useState(false);
  const [tempSelectedSchema, setTempSelectedSchema] = useState<string>("INT");
  const [notifyPopupSizeMapping, setNotifyPopupSizeMapping] =
    useState<SizeMapping | null>(null);

  const handleClose = useCallback(() => {
    setNotifyPopupSizeMapping(null);
    onClose();
  }, [onClose]);

  const {
    availableSchemaMappings,
    selectedSizeSchemaMapping,
    sizeMappings,
    hasMultipleSchemas,
    selectedSchema,
    handleSelectSchema,
    formatBrandSize,
    formatTranslatedSize,
  } = useSizeSchemaFilter({
    variations,
    locale,
    selectedSchema: externalSchema,
    onSchemaChange,
  });

  const productImage = product.images?.[0] || "";

  return (
    <Modal
      isOpen={isOpen}
      hidden={hidden}
      onClose={handleClose}
      fullWidth
      fullHeight
      fullHeightContent
      overFlow="hidden"
    >
      <ContentWrapper data-dropdown-panel>
        <Flex
          alignItems="center"
          justifyContent="center"
          pb={2}
          pt={3}
          mx={3}
          sx={{
            position: "relative",
          }}
        >
          <Heading
            as="h2"
            fontFamily="PlusJakartaSans"
            fontSize={18}
            fontWeight={600}
            color="tone.black"
          >
            <Trans>Select your size</Trans>
          </Heading>
          <CloseButtonWrapper>
            <CloseButton
              onClick={handleClose}
              iconSize={24}
              sx={{
                padding: 0,
                justifyContent: "end",
              }}
            />
          </CloseButtonWrapper>
        </Flex>
        <Flex pt={3} px={3} sx={{ gap: 16 }}>
          <ProductImage src={productImage} alt={product.name} />
          <Flex flexDirection="column" flex={1} pt={3}>
            <Heading as="h3" fontSize={16} fontWeight={600} lineHeight={1.5}>
              {product.brand.name}
            </Heading>
            <Text fontSize={14}>{product.name}</Text>
            <Flex alignItems="center" sx={{ gap: 8 }}>
              <Text fontSize={14} fontWeight={600} color="tone.black">
                <Price price={product.price} />
              </Text>
              <Text fontSize={14} color="tone.anthracite">
                <Price price={product.regular_price} />
                {"*"}
              </Text>
            </Flex>
            {hasSizeGuide && (
              <Text
                fontSize={16}
                textDecoration={"underline"}
                onClick={onOpenSizeGuide}
                sx={{ marginTop: "auto", textAlign: "right" }}
              >
                <Trans>Size guide</Trans>
              </Text>
            )}
          </Flex>
        </Flex>

        <SchemaSelectorRow>
          <Text fontSize={16} lineHeight={1.5} color="tone.anthracite">
            <Trans>Brand size</Trans>
          </Text>
          {hasMultipleSchemas ? (
            <SchemaSelectorButton
              type="button"
              onClick={() => {
                setTempSelectedSchema(selectedSchema);
                setIsSchemaDrawerOpen(true);
              }}
            >
              {selectedSizeSchemaMapping?.title}
            </SchemaSelectorButton>
          ) : (
            <Text fontSize={14} color="tone.anthracite">
              {selectedSizeSchemaMapping?.title}
            </Text>
          )}
        </SchemaSelectorRow>

        <SizeListContainer
          scrollLocked={isSchemaDrawerOpen}
          hasSizeFitLabel={!!sizeFitLabel}
        >
          {sizeMappings.map((sizeMap) => {
            const isSelected = sizeMap.variationId === selectedVariationId;
            const brandSizeFormatted = formatBrandSize(sizeMap);
            const translatedSizeFormatted = formatTranslatedSize(sizeMap);
            const stockText = getStockText?.(sizeMap.stockQuantity);

            const isOutOfStock = sizeMap.stockQuantity === 0;

            const handleRowClick = () => {
              if (isOutOfStock) {
                setNotifyPopupSizeMapping(sizeMap);
                onBackInStockPopupChange?.(true);
              } else {
                onSelectVariation(sizeMap.variationId);
              }
            };

            return (
              <SizeRow
                key={sizeMap.variationId}
                type="button"
                isSelected={isSelected}
                isOutOfStock={isOutOfStock}
                onClick={handleRowClick}
              >
                <BrandSizeCell
                  isSelected={isSelected}
                  isOutOfStock={isOutOfStock}
                >
                  {brandSizeFormatted}
                </BrandSizeCell>
                <MappedSizeCell>
                  <MappedSizeValue
                    isSelected={isSelected}
                    isOutOfStock={isOutOfStock}
                  >
                    {translatedSizeFormatted}
                  </MappedSizeValue>
                  {isOutOfStock ? (
                    <NotifyMeIndicator>
                      <Trans>Notify me</Trans>
                    </NotifyMeIndicator>
                  ) : (
                    stockText && (
                      <StockIndicator isSelected={isSelected}>
                        {stockText}
                      </StockIndicator>
                    )
                  )}
                </MappedSizeCell>
              </SizeRow>
            );
          })}
          {sizeFitLabel && sizeFitLabelMessages[sizeFitLabel] && (
            <SizeFitLabelBanner fixedInSizeOverlay>
              <Text fontSize={14} color="tone.black">
                <TransComponent
                  id={sizeFitLabelMessages[sizeFitLabel].id}
                  message={sizeFitLabelMessages[sizeFitLabel].message}
                />
              </Text>
            </SizeFitLabelBanner>
          )}
        </SizeListContainer>

        <DrawerFooter {...cnstrcItem}>
          {!addToCartLoading ? (
            <Button
              variant="primary"
              {...(cnstrcItem && { "data-cnstrc-btn": "add_to_cart" })}
              onClick={async () => {
                const success = await onAddToCart();
                if (success) {
                  handleClose();
                }
              }}
              disabled={!selectedVariationId}
              fullWidth
            >
              <Trans>Add to bag</Trans>
            </Button>
          ) : (
            <Button variant="primary" disabled fullWidth>
              <Trans>Adding to bag</Trans>
            </Button>
          )}
        </DrawerFooter>

        <Overlay
          isOpen={isSchemaDrawerOpen}
          zIndex={theme.zIndex.drawer + 1}
          onClick={() => setIsSchemaDrawerOpen(false)}
        />

        <SchemaDrawerContainer isOpen={isSchemaDrawerOpen}>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            p={3}
            sx={{ position: "relative", borderBottom: `1px solid #ddd` }}
          >
            <Heading
              as="h2"
              fontFamily="PlusJakartaSans"
              fontSize={18}
              fontWeight={600}
              color="tone.black"
            >
              <Trans>Update size schema</Trans>
            </Heading>
            <CloseButton
              onClick={() => setIsSchemaDrawerOpen(false)}
              iconSize={24}
              sx={{
                padding: 0,
                justifyContent: "end",
              }}
            />
          </Flex>

          <SchemaList>
            {availableSchemaMappings.map((schemaMap) => {
              const isSelected = schemaMap.key === tempSelectedSchema;
              return (
                <SchemaOption
                  key={schemaMap.key}
                  type="button"
                  isSelected={isSelected}
                  onClick={() => setTempSelectedSchema(schemaMap.key)}
                >
                  {schemaMap.title}
                </SchemaOption>
              );
            })}
          </SchemaList>

          <DrawerFooter>
            <Button
              variant="primary"
              fullWidth
              onClick={() => {
                handleSelectSchema(tempSelectedSchema);
                setIsSchemaDrawerOpen(false);
              }}
            >
              <Trans>Update</Trans>
            </Button>
          </DrawerFooter>
        </SchemaDrawerContainer>

        {notifyPopupSizeMapping && (
          <BackInStockNotification
            isOpen={!!notifyPopupSizeMapping}
            onClose={() => {
              setNotifyPopupSizeMapping(null);
              onBackInStockPopupChange?.(false);
            }}
            product={product}
            sizeMapping={notifyPopupSizeMapping}
            selectedSize={`${formatBrandSize(notifyPopupSizeMapping)}${
              formatTranslatedSize(notifyPopupSizeMapping, true)
                ? ` / ${formatTranslatedSize(notifyPopupSizeMapping, true)}`
                : ""
            }`}
            isMobile={true}
          />
        )}
      </ContentWrapper>
    </Modal>
  );
};

export default MobileSizeSelectorOverlay;
